<template>
  <div class="step-container">
    <!-- Header Section -->
    <StepHeader :currentStep="currentStep" :steps="steps" />

    <!-- Content Section -->
    <div class="content-section">
      <div class="scenarios-grid">
        <!-- Available Scenarios -->
        <div class="scenarios-column">
          <h3 class="column-title">{{ lang.W00032 }}</h3>

          <div class="wizard-table-container">
            <!-- Header Row -->
            <div class="wizard-table-header">
              <div>{{ lang.W00035 }}</div>
              <div>{{ lang.W00034 }}</div>
            </div>
            <div class="wizard-table-body">
              <div
                v-for="scenario in availableScenarios"
                :key="scenario.ScenarioId"
                class="wizard-table-row"
                :class="{
                  'disabled': (scenario.disabled || readonly),
                  'selectable': (!scenario.disabled && !readonly)
                }"
                @click="selectScenario(scenario)"
              >
                <div class="wizard-table-cell">{{ scenario.ScenarioId }}</div>
                <div class="wizard-table-cell">{{ scenario.ScenarioName }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Selected Scenarios -->
        <div class="scenarios-column selected-column">
          <div class="selected-content">
            <div class="selected-header">
              <h3 class="column-title">{{ lang.W00033 }}</h3>

              <div class="wizard-table-container">
                <!-- Header Row -->
                <div class="wizard-table-header">
                  <div>{{ lang.W00035 }}</div>
                  <div>{{ lang.W00034 }}</div>
                </div>
                <div class="wizard-table-body">
                  <div
                    v-for="scenario in localFormData.scenarios"
                    :key="scenario.ScenarioId"
                    class="wizard-table-row selected"
                  >
                    <div class="wizard-table-cell">{{ scenario.ScenarioId }}</div>
                    <div class="wizard-table-cell">{{ scenario.ScenarioName }}</div>
                    <div class="wizard-table-cell">
                      <button
                        class="remove-button"
                        @click="removeScenario(scenario.ScenarioId)"
                        :disabled="readonly"
                      >
                        <svg class="remove-icon" fill="none" viewBox="0 0 24 24" stroke="#666666">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

            </div>         
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "../../../styles/audi-steps.css";
import StepHeader from './StepHeader.vue';
import wizardService from "../../../services/WizardService";

export default {
  name: 'Step2',
  components: {
    StepHeader
  },
  props: {
    formData: { type: Object, required: true },
    currentStep: { type: Number, default: 0 },
    steps: { type: Array, required: true },
    readonly: { type: Boolean, default: false }
  },
  data() {
    return {
      localFormData: structuredClone(this.formData),
      availableScenarios: null,
      lang: {
        W00032: "Available scenarios",
        W00033: "Selected scenarios",
        W00034: "Name",
        W00035: "ID",
      }
    }
  },
  watch: {
    "formData": {
      deep: true,
      handler() {
        this.localFormData = structuredClone(this.formData);
      }
    },
    "localFormData": {
      deep: true,
      handler(newVal) {
        this.$emit('update:formData', newVal);
      }
    },
    "globals.selectedLanguage"() {
      this.globals.getLanguageStrings(this);
    },
  },
  mounted() {
    if (this.globals.selectedLanguage) { 
      this.globals.getLanguageStrings(this);
    }

    this.readScenarios(); 
  },
  methods: {
    validateStep() {
      if(!this.localFormData.scenarios || (this.localFormData.scenarios && this.localFormData.scenarios.length <= 0)) {
        return { valid: false, msg: "Please select a scenario."};
      }
      return { valid: true, msg: null};
    },
    updateField(fieldName, value) {
      const updatedData = {
        ...this.formData,
        [fieldName]: value
      };
      this.$emit('update:formData', updatedData);
    },

    selectScenario(scenario) {
      if(this.readonly) return;
      if (scenario.disabled) return;

      // Check if already selected
      let isAlreadySelected = false;
      if (this.localFormData.scenarios)
        isAlreadySelected = this.localFormData.scenarios.some(s => s.ScenarioId === scenario.ScenarioId);
      if (isAlreadySelected) return;

      // Add to selected scenarios
      this.localFormData.scenarios.push({
        ScenarioId: scenario.ScenarioId,
        ScenarioName: scenario.ScenarioName
      });
    },

    removeScenario(scenarioId) {
      if(this.readonly) return;
      this.localFormData.scenarios = this.localFormData.scenarios.filter(s => s.ScenarioId !== scenarioId);
    },

    async readScenarios() {
      let res = await wizardService.readScenarios(this.formData.configurationGuid);
      if(res.length && res.length > 0) this.availableScenarios = res;
    }
  }
}
</script>

<style lang="css" scoped>
.wizard-table-header {
  grid-template-columns: 1fr 2fr 1fr;
}
.wizard-table-row {
  grid-template-columns: 1fr 2fr 1fr;
}
.wizard-table-container {
  min-width: 30vw;
}

.scenarios-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;

  @media (min-width: 1280px) {
    grid-template-columns: 1fr 1fr;
    gap: 64px;
  }
}

.scenarios-column {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.selected-column {
  @media (min-width: 1280px) {
    min-height: 400px;
  }
}

.selected-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 400px;
}

.selected-header {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.column-title {
  font-size: 18px;
  font-weight: 500;
  color: #252424;
}


.remove-button {
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s;
  border: none;
  background: transparent;
  cursor: pointer;

  &:hover {
    background-color: #e2e8f0;
  }
  &:disabled {
    cursor: not-allowed;
  }
}

.remove-icon {
  width: 16px;
  height: 16px;
}
</style>



// WEBPACK FOOTER //
// Step2.vue?604d86e8